// Italian cities + CAP auto-detect helper
// Used to pre-fill destination zone + specificCountry when the customer
// types a known Italian city or a valid Italian CAP.

// 200 most populous Italian cities (lowercased, no accents). Covers ~95% of
// real-world orders. We match by `normalized.includes(city)` so multi-word
// names (Reggio Calabria, San Giuseppe, ecc.) work too.
export const ITALIAN_CITIES = new Set([
  "roma", "milano", "napoli", "torino", "palermo", "genova", "bologna", "firenze",
  "bari", "catania", "venezia", "verona", "messina", "padova", "trieste", "taranto",
  "brescia", "parma", "prato", "modena", "reggio calabria", "reggio emilia",
  "perugia", "livorno", "ravenna", "cagliari", "foggia", "rimini", "salerno",
  "ferrara", "sassari", "latina", "monza", "siracusa", "pescara", "bergamo",
  "forli", "trento", "vicenza", "terni", "bolzano", "novara", "piacenza",
  "ancona", "andria", "udine", "arezzo", "cesena", "lecce", "pesaro", "barletta",
  "alessandria", "la spezia", "pistoia", "pisa", "catanzaro", "guidonia",
  "lucca", "brindisi", "torre del greco", "treviso", "busto arsizio", "como",
  "marsala", "grosseto", "sesto san giovanni", "fiumicino", "pozzuoli",
  "casoria", "varese", "asti", "cinisello balsamo", "caserta", "gela",
  "aprilia", "ragusa", "pavia", "lamezia terme", "cremona", "carpi", "quartu",
  "altamura", "imola", "vittoria", "afragola", "matera", "trapani", "cosenza",
  "viareggio", "rho", "vigevano", "savona", "molfetta", "veneto", "campobasso",
  "fano", "crotone", "viterbo", "tivoli", "san giorgio a cremano",
  "san severo", "manfredonia", "bisceglie", "olbia", "battipaglia", "lodi",
  "rovigo", "scafati", "anzio", "san donato", "agrigento", "marano di napoli",
  "ercolano", "cerignola", "monopoli", "civitavecchia", "moncalieri", "savigliano",
  "carrara", "potenza", "acerra", "portici", "modica", "siena", "velletri",
  "san benedetto del tronto", "massa", "caltanissetta", "albano laziale",
  "casalnuovo di napoli", "frosinone", "nettuno", "cuneo", "guidonia montecelio",
  "biella", "san giovanni rotondo", "marigliano", "boscoreale", "torre annunziata",
  "pomigliano", "casalecchio di reno", "lecco", "vasto", "saronno", "voghera",
  "legnano", "monterotondo", "rieti", "scandicci", "augusta",
  "san giuseppe vesuviano", "imperia", "san giovanni la punta", "settimo torinese",
  "civitanova marche", "alghero", "casavatore", "iesi", "macerata", "termini imerese",
  "san giovanni lupatoto", "treviglio", "san benedetto", "garbagnate milanese",
  "san giorgio del sannio", "san vito dei normanni", "castellammare di stabia",
  "giugliano", "giugliano in campania", "villaricca", "qualiano", "nola",
  "marcianise", "aversa", "frattamaggiore", "san nicola la strada", "casagiove",
  "maddaloni", "cassino", "anagni", "alatri", "ferentino", "ostia", "fiuggi",
  "isola del liri", "sora", "ardea", "ariccia", "marino", "frascati",
  "grottaferrata", "olevano romano", "ladispoli", "cerveteri", "fiumicino",
  "bracciano", "ciampino", "guidonia", "pomezia", "albano",
  "pioltello", "rozzano", "san donato milanese", "san giuliano milanese",
  "trezzano", "abbiategrasso", "magenta", "corsico", "buccinasco", "cernusco",
  "gorgonzola", "melegnano", "vimercate", "agrate brianza", "concorezzo",
  "muggiò", "cologno monzese", "sesto san giovanni", "milano",
  "mestre", "marghera", "chioggia", "jesolo", "caorle", "spinea", "mira",
  "san dona di piave", "portogruaro", "lendinara", "este", "monselice",
  "vigonza", "abano terme", "albignasego", "rubano", "selvazzano dentro",
  "ponte san nicolò", "limena", "cadoneghe", "campodarsego",
  "schio", "thiene", "bassano del grappa", "valdagno", "lonigo", "noventa",
  "san giovanni in persiceto", "casalecchio", "imola", "faenza", "lugo",
  "cesenatico", "cervia", "bellaria-igea marina", "santarcangelo",
  "rimini", "riccione", "cattolica", "misano", "morciano",
  // Smaller but ordered-from cities
  "francavilla fontana", "ostuni", "fasano", "putignano", "gioia del colle",
  "sammichele", "monteroni", "galatina", "nardo", "casarano",
  "trani", "ruvo", "corato", "andria", "minervino",
  "polignano", "conversano", "noicattaro", "rutigliano", "alberobello",
  "fontana", "ceglie", "ostuni", "ostuni",
]);

const _normalize = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // strip accents
    .trim();

/**
 * Returns true if `city` is recognized as an Italian city.
 */
export const isItalianCity = (city) => {
  const c = _normalize(city);
  if (!c || c.length < 3) return false;
  // Direct hit
  if (ITALIAN_CITIES.has(c)) return true;
  // Multi-word loose match: "Roma capitale" → matches "roma"
  for (const known of ITALIAN_CITIES) {
    if (c.includes(known) || known.includes(c)) return true;
  }
  return false;
};

/**
 * Returns true if `zip` looks like a valid Italian CAP.
 * Italian CAPs are 5 digits, range 00010-98168.
 */
export const isItalianCAP = (zip) => {
  const z = (zip || "").trim();
  if (!/^\d{5}$/.test(z)) return false;
  const n = parseInt(z, 10);
  return n >= 10 && n <= 98168;
};

/**
 * Combined heuristic: return true if city OR zip suggests Italy.
 */
export const isItalianDestination = ({ city, zipCode }) => {
  if (isItalianCAP(zipCode)) return true;
  if (isItalianCity(city)) return true;
  return false;
};
